import React, { useState, useEffect } from 'react';
import api from '../api';
import Pagination from './Pagination';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [activeSection, setActiveSection] = useState('list'); // list, create, import, resetPassword, bulkDelete
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'student'
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [resetUserId, setResetUserId] = useState('');
    const [resetPassword, setResetPassword] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await api.get('/users/students');
            setUsers(res.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            alert('Lỗi khi tải danh sách người dùng');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await api.post('/users', formData);
            setFormData({ name: '', email: '', password: '', role: 'student' });
            setActiveSection('list');
            fetchUsers();
            alert('Tạo người dùng thành công!');
        } catch (error) {
            console.error('Error creating user:', error);
            alert('Lỗi: ' + (error.response?.data?.errors?.[0]?.msg || 'Không thể tạo người dùng'));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (window.confirm(`Bạn có chắc muốn xóa người dùng: ${userName}?`)) {
            try {
                await api.delete(`/users/${userId}`);
                fetchUsers();
                alert('Xóa người dùng thành công!');
            } catch (error) {
                console.error('Error deleting user:', error);
                alert('Lỗi: ' + (error.response?.data?.errors?.[0]?.msg || 'Không thể xóa người dùng'));
            }
        }
    };

    const handleBulkDelete = async () => {
        if (selectedUsers.length === 0) {
            alert('Vui lòng chọn ít nhất một người dùng để xóa');
            return;
        }

        if (window.confirm(`Bạn có chắc muốn xóa ${selectedUsers.length} người dùng đã chọn?`)) {
            try {
                setLoading(true);
                const res = await api.post('/users/bulk-delete', { userIds: selectedUsers });
                setSelectedUsers([]);
                fetchUsers();
                alert(`Đã xóa thành công ${res.data.deletedCount} người dùng!`);
            } catch (error) {
                console.error('Error bulk deleting:', error);
                alert('Lỗi: ' + (error.response?.data?.errors?.[0]?.msg || 'Không thể xóa người dùng'));
            } finally {
                setLoading(false);
            }
        }
    };

    const handleBulkImport = async () => {
        if (!selectedFile) {
            alert('Vui lòng chọn file CSV');
            return;
        }

        const formData = new FormData();
        formData.append('csvFile', selectedFile);

        try {
            setLoading(true);
            const response = await api.post('/users/bulk-import', formData);
            setImportResult(response.data);
            setSelectedFile(null);
            document.getElementById('csv-file-input').value = '';
            fetchUsers();
        } catch (err) {
            console.error('Upload error:', err);
            alert('Lỗi: ' + (err.response?.data?.errors?.[0]?.msg || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await api.put(`/users/reset-password/${resetUserId}`, { newPassword: resetPassword });
            setResetUserId('');
            setResetPassword('');
            setActiveSection('list');
            alert('Đặt lại mật khẩu thành công!');
        } catch (error) {
            console.error('Error resetting password:', error);
            alert('Lỗi: ' + (error.response?.data?.errors?.[0]?.msg || 'Không thể đặt lại mật khẩu'));
        } finally {
            setLoading(false);
        }
    };

    const downloadTemplate = () => {
        // UTF-8 BOM ensures proper encoding
        const BOM = '\uFEFF';
        const rows = [
            'name;email;password',
            'Nguyễn Hữu Hoàng Minh;minh@example.com;password123',
            'Trần Thị Lan Anh;lananh@example.com;password456',
            'Lê Văn Đức;duc@example.com;password789'
        ];
        const csvContent = BOM + rows.join('\n');

        // Create blob with explicit UTF-8 encoding
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'student-import-template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const toggleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(u => u._id));
        }
    };

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination calculations
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

    // Reset to page 1 when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setSelectedUsers([]); // Clear selections when changing pages
    };

    return (
        <div className="space-y-6">
            {/* Header with Action Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-2xl font-semibold text-slate-900">Quản lý người dùng</h2>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setActiveSection('create')}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold shadow transition ${
                            activeSection === 'create'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                        <i className="fas fa-user-plus" aria-hidden></i>
                        Tạo mới
                    </button>
                    <button
                        onClick={() => setActiveSection('import')}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold shadow transition ${
                            activeSection === 'import'
                                ? 'bg-green-600 text-white'
                                : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                        <i className="fas fa-file-upload" aria-hidden></i>
                        Nhập CSV
                    </button>
                    <button
                        onClick={() => setActiveSection('resetPassword')}
                        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold shadow transition ${
                            activeSection === 'resetPassword'
                                ? 'bg-yellow-600 text-white'
                                : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                        <i className="fas fa-key" aria-hidden></i>
                        Đặt lại MK
                    </button>
                    {selectedUsers.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-red-700"
                        >
                            <i className="fas fa-trash-alt" aria-hidden></i>
                            Xóa ({selectedUsers.length})
                        </button>
                    )}
                    <button
                        onClick={() => {
                            setActiveSection('list');
                            setImportResult(null);
                        }}
                        className="inline-flex items-center gap-2 rounded-xl bg-slate-600 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-slate-700"
                    >
                        <i className="fas fa-list" aria-hidden></i>
                        Danh sách
                    </button>
                </div>
            </div>

            {/* Create User Form */}
            {activeSection === 'create' && (
                <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 p-6 shadow-lg">
                    <h3 className="mb-4 text-xl font-semibold text-slate-900">
                        <i className="fas fa-user-plus mr-2 text-indigo-600" aria-hidden></i>
                        Tạo người dùng mới
                    </h3>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">
                                    Họ tên <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="Nguyễn Văn A"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    required
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">
                                    Mật khẩu <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    minLength={6}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                    placeholder="Tối thiểu 6 ký tự"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700">
                                    Vai trò <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                                >
                                    <option value="student">Sinh viên</option>
                                    <option value="admin">Quản trị viên</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                                        Đang tạo...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-save" aria-hidden></i>
                                        Tạo người dùng
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveSection('list')}
                                className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                            >
                                Hủy
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Bulk Import Section */}
            {activeSection === 'import' && (
                <div className="rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 p-6 shadow-lg">
                    <h3 className="mb-4 text-xl font-semibold text-slate-900">
                        <i className="fas fa-file-upload mr-2 text-green-600" aria-hidden></i>
                        Nhập hàng loạt từ CSV
                    </h3>
                    <div className="mb-4 rounded-xl bg-white/80 p-4">
                        <h4 className="mb-2 font-semibold text-slate-800">Hướng dẫn:</h4>
                        <ul className="list-inside list-disc space-y-1 text-sm text-slate-600">
                            <li>File CSV phải có các cột: name, email, password (phân cách bởi dấu ;)</li>
                            <li>Mật khẩu tối thiểu 6 ký tự</li>
                            <li className="font-semibold text-green-700">✓ Hỗ trợ tên tiếng Việt có dấu</li>
                        </ul>
                        <button
                            onClick={downloadTemplate}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700"
                        >
                            <i className="fas fa-download" aria-hidden></i>
                            Tải mẫu CSV
                        </button>
                    </div>
                    <div className="space-y-4">
                        <input
                            id="csv-file-input"
                            type="file"
                            accept=".csv"
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                            className="block w-full cursor-pointer text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-green-100 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-green-700 hover:file:bg-green-200"
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={handleBulkImport}
                                disabled={!selectedFile || loading}
                                className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-green-700 disabled:opacity-50"
                            >
                                {loading ? 'Đang tải lên...' : 'Tải lên và Nhập'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveSection('list')}
                                className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                            >
                                Hủy
                            </button>
                        </div>
                    </div>

                    {/* Import Result */}
                    {importResult && (
                        <div className="mt-4 rounded-xl bg-white p-4">
                            <h4 className="mb-2 font-semibold text-slate-800">Kết quả:</h4>
                            <div className="space-y-1 text-sm">
                                <p>Tổng: {importResult.summary.total}</p>
                                <p className="text-green-600">Thành công: {importResult.summary.success}</p>
                                <p className="text-red-600">Thất bại: {importResult.summary.failed}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Reset Password Section */}
            {activeSection === 'resetPassword' && (
                <div className="rounded-2xl bg-gradient-to-br from-yellow-50 to-orange-50 p-6 shadow-lg">
                    <h3 className="mb-4 text-xl font-semibold text-slate-900">
                        <i className="fas fa-key mr-2 text-yellow-600" aria-hidden></i>
                        Đặt lại mật khẩu người dùng
                    </h3>
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                                Chọn người dùng <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={resetUserId}
                                onChange={(e) => setResetUserId(e.target.value)}
                                required
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-100"
                            >
                                <option value="">-- Chọn người dùng --</option>
                                {users.map((user) => (
                                    <option key={user._id} value={user._id}>
                                        {user.name} ({user.email})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                                Mật khẩu mới <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="password"
                                value={resetPassword}
                                onChange={(e) => setResetPassword(e.target.value)}
                                required
                                minLength={6}
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-100"
                                placeholder="Tối thiểu 6 ký tự"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center gap-2 rounded-xl bg-yellow-600 px-6 py-3 text-sm font-semibold text-white shadow transition hover:bg-yellow-700 disabled:opacity-50"
                            >
                                {loading ? 'Đang xử lý...' : 'Đặt lại mật khẩu'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveSection('list')}
                                className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                            >
                                Hủy
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* User List */}
            {activeSection === 'list' && (
                <div className="space-y-4">
                    {/* Search Bar */}
                    <div className="rounded-xl bg-white p-4 shadow">
                        <div className="flex items-center gap-2">
                            <i className="fas fa-search text-slate-400" aria-hidden></i>
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo tên hoặc email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="flex-1 border-0 bg-transparent text-sm text-slate-700 focus:outline-none"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-8 text-center">
                            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
                            <p className="mt-2 text-sm text-slate-600">Đang tải...</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-lg">
                                <table className="w-full">
                                    <thead className="bg-gradient-to-r from-slate-700 to-slate-600 text-white">
                                        <tr>
                                            <th className="px-6 py-4 text-left">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                                    onChange={toggleSelectAll}
                                                    className="h-4 w-4 cursor-pointer rounded border-slate-300"
                                                />
                                            </th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold">Họ tên</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold">Email</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold">Vai trò</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold">Ngày tạo</th>
                                            <th className="px-6 py-4 text-center text-sm font-semibold">Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 bg-white">
                                        {currentUsers.map((user) => (
                                            <tr key={user._id} className="transition hover:bg-slate-50">
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(user._id)}
                                                        onChange={() => toggleUserSelection(user._id)}
                                                        className="h-4 w-4 cursor-pointer rounded border-slate-300"
                                                    />
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                    {user.name}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-600">{user.email}</td>
                                                <td className="px-6 py-4 text-sm">
                                                    <span
                                                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                                            user.role === 'admin'
                                                                ? 'bg-red-100 text-red-800'
                                                                : 'bg-green-100 text-green-800'
                                                        }`}
                                                    >
                                                        {user.role === 'admin' ? 'Quản trị viên' : 'Sinh viên'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-600">
                                                    {new Date(user.createdAt).toLocaleDateString('vi-VN')}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button
                                                        onClick={() => handleDeleteUser(user._id, user.name)}
                                                        className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow transition hover:bg-red-700"
                                                    >
                                                        <i className="fas fa-trash" aria-hidden></i>
                                                        Xóa
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {currentUsers.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                                                    {searchTerm ? 'Không tìm thấy người dùng' : 'Chưa có người dùng nào'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {filteredUsers.length > 0 && (
                                <div className="mt-4">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        itemsPerPage={itemsPerPage}
                                        totalItems={filteredUsers.length}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserManagement;
